import { useEffect, useState } from "react";
import config from "config";
import useJumboFormsCall from "hooks/useJumboFormsCall.js";
import useRemoveMailFunction from "hooks/useRemoveMailFunction.js";
import ShareComponent from "components/Reusable/SideBar/Share/ShareSideComponent.js";
import SecuritySettings from "../Popups/SecuritySettings.js";
import PaymentSettings from "../Popups/PaymentSettings.js";
import EditEmailModal from "components/Reusable/EditEmailModal/EditEmailModal.js";
import usePickerQueryParamActions from "../Picker/usePickerQueryParamActions.ts";
import loader from "helpers/loaderUrl.js";
import { useVideoPreviewAction } from "components/Reusable/FilesActionGroup/VideoPreviewAction.js";
import { useSaveInboxMail } from "hooks/useSaveInboxMail.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import useInbox from "hooks/useInbox.ts";
import { useToasts } from "react-toast-notifications";
import { useModal } from "contexts/modalContext/modalContext.js";
import { LogModal } from "../Embed/LogModal.js";
import { useUser } from "contexts/userContext/userContext.js";
import LabelAsPopup from "../Popups/LabelAsPopup.tsx";
import RemoveExpiredMailModal from "./RemoveExpiredMailModal.tsx";
import { calcCounters } from "../helpers.js";
import { useAppSelector } from "store/hooks.ts";
import { selectTParam } from "store/selectors/inboxSelectors.ts";
import { GetUserUsedStorageApiRequest } from "api/UsersController.ts";

export const useInboxActionsList = ({ mailData, listItemRef }) => {
  const { handleClick, isSaved, loading } = useSaveInboxMail({ mailData });
  const { getPath, getTextByKey } = useContent();
  const { getUserData, updateUserUsedStorage } = useUser();

  const {
    getSearchParams,
    getFTPAPIsettings,
    setMailDataProp,
    removeMailsAction,
    removeMailFromState,
    setMailsCount,
    removeMailForever,
    getSelectedList,
  } = useInbox();
  const pageType = useAppSelector(selectTParam);
  const { setModal } = useModal();
  const { addToast } = useToasts();
  const { openIframe } = usePickerQueryParamActions();
  const [isRestoring, setIsRestoring] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const {
    apiCall,
    ftpCall,
    apiLoading,
    setApiLoading,
    ftpLoading,
    setFtpLoading,
  } = useJumboFormsCall({ mailData });

  const [requesting, setRequesting] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);

  useEffect(() => {
    return () => {
      setRequesting(false);
      setApiLoading(false);
      setFtpLoading(false);
    };
  }, []);

  const editEmailModal = {
    name: "edit-email",
    iconName: "edit-pen-solid",
    clickAction: () => {
      setModal({
        modal: EditEmailModal,
        props: {
          subject: mailData?.subject,
          message: mailData?.text,
          emailId: mailData?.id,
          userNameDisplay: mailData?.displaySenderName,
          subjectDisplay: mailData?.displayEmailSubject,
          callbackUpdateProps: ({
            subjectValue,
            messageValue,
            displaySenderName,
            displaySubject,
          }) => {
            setMailDataProp(
              mailData?.id,
              ["subject", "text", "displaySenderName", "displayEmailSubject"],
              [subjectValue, messageValue, displaySenderName, displaySubject]
            );
          },
        },
      });
    },
  };

  const removeMailFunc = useRemoveMailFunction({
    activeArray: [mailData?.id],
    status: mailData?.mailStatus,
    setLoading: setRequesting,
    filesSize: mailData?.filesSize,
  });

  const hanldeVideoPreviewCall = useVideoPreviewAction({
    mailId: mailData?.id,
    setLoading: setVideoLoading,
  });

  const removeAction = (mailData?.relatedTo !== "currentUser"
    ? getUserData().isTopUser || getUserData().subUserRole === "Admin"
    : true) && {
    name: "remove-mail-completely",
    iconName: requesting ? null : "remove",
    iconSrc: requesting ? loader : null,
    clickAction:
      mailData?.mailStatus === "removed"
        ? () =>
            setModal({
              modal: RemoveExpiredMailModal,
              props: {
                encryptedMailIds: [mailData?.id],
                // cb: () => {},
              },
            })
        : removeMailFunc,
  };

  const videoPreviewAction = mailData?.doesContainVideo
    ? {
        name: "video-preview",
        iconName: videoLoading ? null : "video-preview-2",
        iconSrc: videoLoading ? loader : null,
        clickAction: () => {
          hanldeVideoPreviewCall();
        },
      }
    : null;

  const brandingAction = {
    name: "mail-branding",
    iconName: "brand",
    clickAction: () => {
      window.open(
        `${config.baseurl}${getPath()}gallery/${mailData?.id}/select-cover`
      );
    },
  };
  const convertToPickerAction =
    getUserData()?.HideJumboPicker || mailData?.mailStatus === "uploading"
      ? null
      : {
          name: "convert-to-picker",
          iconName: "newInbox/Picker/picker-gray",
          clickAction: () => {
            if (mailData?.canConvertToPicker) {
              if (mailData?.isSaved && !mailData?.isSavedByUser) {
                addToast(getTextByKey("converting-disabled-notification"), {
                  appearance: "error",
                });
              } else {
                openIframe("convert", {
                  mailId: mailData?.id,
                });
              }
            } else {
              addToast(getTextByKey("converting-not-allowed-no-images"), {
                appearance: "error",
              });
            }
          },
        };

  const saveAction =
    mailData?.isEmbedManuallyDeleted ||
    (isSaved ? mailData?.isOutbox || mailData?.isJForm : false) ||
    (mailData?.mailStatus === "pendingRemoved" && !mailData?.isOutbox) ||
    mailData?.mailStatus === "uploading"
      ? null
      : {
          name: (
            getSearchParams()?.f === "jform"
              ? isSaved
              : mailData?.mailStatus === "saved"
          )
            ? "unsave-mail"
            : "save-mail",
          iconName: loading
            ? null
            : (
                getSearchParams()?.f === "jform"
                  ? isSaved
                  : mailData?.mailStatus === "saved"
              )
            ? "unsave-solid"
            : "save-solid",
          iconSrc: loading ? loader : null,
          clickAction: () => {
            handleClick();
          },
        };

  const shareAction =
    (mailData?.security?.isSharingDisabled && pageType === "received") ||
    mailData?.mailStatus === "uploading"
      ? null
      : {
          name: "share-mail",
          iconName: "share-solid",
          clickAction: () =>
            setModal({
              modal: ShareComponent,
              props: { mailData: mailData },
            }),
        };

  const securityAction = {
    name: "mail-security",
    iconName: "alock",
    clickAction: () =>
      setModal({
        modal: SecuritySettings,
        props: { data: mailData },
      }),
  };

  const paymentAction = getUserData()?.HideJumboPay
    ? null
    : {
        name: "mail-payment",
        iconName: "jumbopay",
        clickAction: () =>
          setModal({
            modal: PaymentSettings,
            props: { data: mailData },
          }),
      };

  const logAction = {
    name: "alog",
    iconName: "alog",
    clickAction: () => {
      setModal({
        modal: LogModal,
        props: { deliveryNumber: mailData?.deliveryNumber },
      });
    },
  };

  const ftpAction = {
    name: "ftp",
    iconName: ftpLoading ? null : "ftp",
    iconSrc: ftpLoading ? loader : null,
    clickAction: () => {
      getFTPAPIsettings().ftp
        ? ftpCall()
        : addToast(getTextByKey("ftp-disabled-notification"), {
            appearance: "info",
          });
    },
  };

  const apiAction = {
    name: "api",
    iconName: apiLoading ? null : "api",
    iconSrc: apiLoading ? loader : null,
    clickAction: () => {
      getFTPAPIsettings().api
        ? apiCall()
        : addToast(getTextByKey("api-disabled-notification"), {
            appearance: "info",
          });
    },
  };

  const labelAction = {
    name: "label-as",
    iconName: "newInbox/label",
    clickAction: async () => {
      setModal(() => <LabelAsPopup selectedMails={[mailData?.id]} />);
    },
  };

  const removeForeverAction =
    mailData?.deletedUserFullName ===
    `${getUserData()?.UserFirstName} ${getUserData()?.UserLastName}`
      ? {
          name: "remove-mail-forever",
          iconName: isRemoving ? null : "remove",
          iconSrc: isRemoving ? loader : null,
          clickAction: async () => {
            const response = await removeMailForever({
              encryptedMailIds: getSelectedList()?.length
                ? getSelectedList()
                : [mailData?.id],
              setLoadingStatus: setIsRemoving,
            });

            if (response?.status === "success") {
              addToast(getTextByKey("mail-removed-forever"), {
                appearance: "success",
              });
            } else {
              addToast(getTextByKey("mail-not-removed-forever"), {
                appearance: "error",
              });
            }
          },
        }
      : null;

  const restoreAction =
    mailData?.deletedUserFullName ===
    `${getUserData()?.UserFirstName} ${getUserData()?.UserLastName}`
      ? {
          name: "restore",
          iconName: isRestoring ? null : "newinbox/clock-back",
          iconSrc: isRestoring ? loader : null,
          clickAction: async () => {
            const response = await removeMailsAction({
              mailsArray: [mailData?.id],
              mailStatus: mailData?.mailStatus,
              isUndo: true,
              setLoadingStatus: setIsRestoring,
            });
            const userUsedStorage = await GetUserUsedStorageApiRequest();
            updateUserUsedStorage({
              UserUsedStorage: userUsedStorage?.data,
            });
            if (response.status === "success") {
              const updatedCounters = calcCounters(mailData, getUserData());
              setMailsCount(updatedCounters);

              if (listItemRef?.current) {
                listItemRef?.current?.classList?.add("remove-mail-anim");
              }
              setTimeout(() => removeMailFromState(mailData?.id), 300);
            }
          },
        }
      : null;

  const trashActions = [
    labelAction,
    removeForeverAction,
    mailData?.mailStatus !== "removed" ? restoreAction : null,
  ];

  const formsActions = [
    editEmailModal,
    saveAction,
    videoPreviewAction,
    brandingAction,
    shareAction,
    logAction,
    ftpAction,
    apiAction,
    labelAction,
    convertToPickerAction,
    removeAction,
  ];

  const actionsEnum = {
    received: mailData?.isJForm
      ? formsActions
      : mailData?.security?.previewFiles
      ? [labelAction, shareAction]
      : [labelAction, shareAction, saveAction],
    sent: [
      editEmailModal,
      saveAction,
      videoPreviewAction,
      brandingAction,
      securityAction,
      paymentAction,
      convertToPickerAction,
      labelAction,
      shareAction,
      removeAction,
    ],
    forms: formsActions,
  };

  const expiredMailActions = [logAction, labelAction, removeAction];

  if (pageType === "trash") {
    return trashActions;
  }

  if (mailData?.mailStatus === "removed") {
    return expiredMailActions;
  }

  if (getSearchParams()?.f === "jform") {
    return actionsEnum.forms;
  }

  if (pageType === "received") {
    return actionsEnum.received;
  }

  if (pageType === "sent") {
    //remove security and payment actions from supervisor view
    const isUserSupervisor = getUserData().subUserRole === "Supervisor";
    const isUserSender =
      mailData?.SenderInfo?.senderEmail === getUserData().UserName;
    if (isUserSupervisor && !isUserSender) {
      const actionList = actionsEnum.sent?.filter(
        (action) =>
          action?.name !== "mail-security" && action?.name !== "mail-payment"
      );

      return actionList;
    } else {
      return actionsEnum.sent;
    }
  }

  if (pageType === "all") {
    return actionsEnum;
  }

  // return actionsEnum[pageType];
};

export default useInboxActionsList;
